import React, {useEffect} from 'react';
import { ILocation } from '../../Api/Models/Customer';
import { useGetLocationsQuery } from '../../Api/Api';
import {Combobox, ComboboxProps, Field, Label, Option, OptionGroup} from '@fluentui/react-components';
import { useLocalization } from '../Helpers/useLocalization';

interface ILocationSelectionProps {
    selectedLocation: string | undefined;
    onChange: (location?: ILocation) => void;
    label?: string;
}

const LocationSelection: React.FC<ILocationSelectionProps> = (props) => {
    const locations = useGetLocationsQuery();
    const L = useLocalization();
    const [selected, setSelected] = React.useState<string[]>(['Other']);
    const [selectedLabel, setSelectedLabel] = React.useState<string>('Other');
    const [customSearch, setCustomSearch] = React.useState<string>();
    
    const [matchingOptions, setMatchingOptions] = React.useState<ILocation[]>([]);

    useEffect(() => {
        if (!locations.data || locations.data.items.length === 0) {
            setMatchingOptions([]);
            return;
        }

        if (customSearch) {
            const searchTerms = customSearch
                .toLowerCase()
                .split(' ')
                .filter(term => term.trim() !== '');

            const scoredLocations = locations.data.items
                .map(location => {
                    const fields = [
                        location.name,
                        location.address,
                        location.city,
                        location.state,
                        location.postalCode
                    ].map(f => f?.toLowerCase() ?? '');

                    // Count how many search terms match any field
                    const score = searchTerms.reduce((acc, term) => {
                        return acc + (fields.some(field => field.includes(term)) ? 1 : 0);
                    }, 0);

                    return { location, score };
                })
                .filter(item => item.score > 0)
                .sort((a, b) => b.score - a.score)
                .map(item => item.location);

            setMatchingOptions(scoredLocations);
        } else {
            setMatchingOptions(locations.data.items);
        }
    }, [locations.data, customSearch]);

    const onChange: ComboboxProps["onChange"] = (event) => {
        const value = event.target.value.trim();
        setCustomSearch(value);
        
    };
    
    React.useEffect(() => {
        if (props.selectedLocation) {
            setSelected([props.selectedLocation]);
            const l = locations.data?.items.find((l) => l.index.toString() === props.selectedLocation);
            setSelectedLabel(l?.name ?? 'Other');
        } else {
            setSelected(['Other']);
            setSelectedLabel('Other');
        }
    }, [props.selectedLocation, locations.data?.items]);
    
    const getName = (locationId: string | undefined) => {
        const location = locations.data?.items.find((l) => l.index.toString() === locationId);
        return location?.name;
    }

    return (<Field>
        <Label>{props.label}</Label>
        <Combobox
            placeholder={L('localization_address_select_location')}
            positioning={{ autoSize: 'height' }}
            onChange={onChange}
            disabled={locations.isLoading}
            value={getName(props.selectedLocation)}
            defaultValue={'Other'}
            onOptionSelect={(ev, data) => {
                const location = locations.data?.items.find((l) => l.index.toString() === data.optionValue);
                props.onChange(location);
            }}
            // placeholder={selectedLabel}
        >
            {(() => {
                const crmOptions = matchingOptions?.filter(l => l.type !== 'PreviousOrderAddress') ?? [];
                const previousOrderOptions = matchingOptions?.filter(l => l.type === 'PreviousOrderAddress') ?? [];
                const renderOption = (location: ILocation) => (
                    <Option text={location.name ?? ''} key={`location_${location.index}`} value={location.index.toString()}>
                        <p style={{margin: 0, padding: 0}}>
                            <span style={{fontWeight: 'bold'}}>{location.name}</span> <br />
                            <span style={{fontSize: '90%', opacity: 0.7}}>
                                {location.address}<br />
                                {location.address2 && <>{location.address2}<br /></>}
                                {location.city}, {location.state} {location.postalCode}
                            </span>
                        </p>
                    </Option>
                );
                return <>
                    {crmOptions.length > 0 && <OptionGroup>{crmOptions.map(renderOption)}</OptionGroup>}
                    {previousOrderOptions.length > 0 && (
                        <OptionGroup label={L('localization_address_previous_order_addresses')}>
                            {previousOrderOptions.map(renderOption)}
                        </OptionGroup>
                    )}
                </>;
            })()}
            <Option value='Other' text={L('localization_address_other')}>{L('localization_address_other')}</Option>
        </Combobox>
    </Field>)
}

export default LocationSelection;